import {
  Color3,
  Color4,
  ColorCurves,
  CreateBox,
  CreateCylinder,
  CreatePlane,
  CreateSphere,
  DefaultRenderingPipeline,
  DirectionalLight,
  HemisphericLight,
  ImageProcessingConfiguration,
  Matrix,
  Mesh,
  Quaternion,
  ShaderMaterial,
  CascadedShadowGenerator,
  PBRMaterial,
  PointLight,
  ThinEngine,
  TransformNode,
  Vector3,
  Vector4,
  VertexData,
  type AbstractMesh,
  type Camera,
  type Scene,
} from "@babylonjs/core";
import {
  COLLIDERS,
  LANDMASSES,
  OUTPOSTS,
  PILE_INSET,
  ROCKS,
  SUPPLY_BARRELS,
  TAU,
  WORLD,
  LANTERN_ORIGIN,
  LANTERN_ISLE,
  LANTERN_PATHS,
  SKULL_ORIGIN,
  SKULL_ISLE,
  clamp,
  cliffColumns,
  dockPiles,
  hash2,
  isTrodden,
  propPlacement,
  smoothstep,
  skullCliffRockWeight,
  supplyBarrelWorld,
  terrainHeight,
  valueNoise,
  type Landmark,
  type Landmass,
  type Stair,
  type WeatherSample,
} from "@tiny-seas/shared";
import type { Outpost, IslandTheme } from "@tiny-seas/shared";
import { outpostBuildingSeat } from "@tiny-seas/shared";
import { TERRAIN } from "./themes.ts";
import { Weather } from "./weather.ts";
import { Materials, SURFACE_PROFILES, boxProjectUVs, type PaletteKey } from "./materials.ts";
import { skyFragmentShader, SKY_VS, waterShaders } from "./shaders.ts";
import { bakeShoreMap, type ShoreMap } from "./shore.ts";
import { spawnModel, type ModelInstance, type SceneryName } from "./models.ts";
import { DAY_HEMI_INTENSITY, DAY_SUN_INTENSITY, setupDaylightIBL } from "./lighting.ts";
import { createTerrainMaterial } from "./terrain-material.ts";
import { spawnScenery } from "./scenery.ts";
import { getSmokeTexture } from "./surface-textures.ts";
import { LanternScene, type LanternVisualState } from "./lantern.ts";
import { SkullScene, skullGroundWear } from "./skull.ts";
import { SKULL_LIGHT_ANCHORS } from "./skull-fort.ts";
import { terrainStep, terrainSurfaceHeight } from "./terrain-surface.ts";
import { OceanCaptures } from "./ocean-reflections.ts";
import { attachContactShadows, type ContactShadows } from "./contact-shadows.ts";
import { DAY_SKY_ZENITH as SKY_ZENITH, DAY_SKY_HORIZON as FOG_DAY, DAY_SUN_DIRECTION } from "./atmosphere.ts";
import { GRAPHICS_PRESETS, type ContactShadowSamples, type GraphicsPreset } from "./graphics.ts";
import { staticSceneryBatch } from "./static-scenery.ts";
import { CascadeShadowCulling } from "./cascade-culling.ts";
import { boulderMesh, cliffMeshes, gullFlap, gullWing } from "./landforms.ts";
import { IslandFixtures } from "./fixtures.ts";
import { GAME_WAVES, REDUCED_MOTION_WAVES, WAVE_COMPONENTS, blowWind, cloneWaves, shipMotion, waterHeight, waveSurface, type ShipMotion, type WaveParams, type WaveSurface } from "./waves.ts";
import { SIM_HULLS, WaterSim, type HullSource } from "./water-sim.ts";
import { OCEAN_SNAP, oceanMesh } from "./ocean-mesh.ts";
import { SeaSpray } from "./sea-spray.ts";

/** Height of the beacon tower's fire basket above its base (tools/models/bastion.py). */
const BEACON_FLAME_Y = 22.8;

/** Clear near-island materials while distant terrain fades before its 2.4 km culling boundary. */
const FOG_DENSITY = 0.001;
const FOG_UNDER = new Color3(0.08, 0.35, 0.45);

/** Independently hideable world subsystems (developer labs); each one's meshes and per-frame update. */
export const ENVIRONMENT_LAYERS = ["water", "sky", "terrain", "landforms", "vegetation", "structures", "wildlife", "weather"] as const;
export type EnvironmentLayer = (typeof ENVIRONMENT_LAYERS)[number];

/** Static world: sky, lights, sea, islands, outpost and landmarks. */
export class Environment {
  readonly sun: DirectionalLight;
  readonly mats: Materials;
  private water!: Mesh;
  private waterMat!: ShaderMaterial;
  private skyDome!: Mesh;
  private skyMat!: ShaderMaterial;
  private readonly oceanCaptures: OceanCaptures;
  private readonly shadow: CascadedShadowGenerator;
  private readonly shadowCulling: CascadeShadowCulling;
  private readonly shore: ShoreMap;
  private readonly casters: AbstractMesh[] = [];
  private readonly tuftBatches = new Map<string, { land: Landmass; variant: number; matrices: number[] }>();
  private readonly sceneryPrototypes = new Map<string, ModelInstance>();
  private readonly sceneryBatches = new Map<number, { source: Mesh; matrices: number[] }>();
  private readonly regionalMeshes = new Map<Mesh, string>();
  private windmillBlades: TransformNode[] = [];
  private lampMats: PBRMaterial[] = [];
  private time = 0;
  private visualTime = 0;
  private waveParams = cloneWaves(GAME_WAVES);
  private surface = waveSurface(this.waveParams);
  /** The water's `waveK`/`waveA` uniform arrays, rewritten in place (`uploadComponents`). */
  private readonly waveK = new Array<number>(WAVE_COMPONENTS * 4).fill(0);
  private readonly waveA = new Array<number>(WAVE_COMPONENTS * 4).fill(0);
  private sim!: WaterSim;
  private spray!: SeaSpray;
  private hullSources: readonly HullSource[] = [];
  /** (centre x, centre z, extent, 1 while the simulation runs), updated in place for the water shader. */
  private readonly simWindow = new Vector4(0, 0, 1, 0);
  private underwater = false;
  readonly weather: Weather;
  private readonly terrainMeshes: { mesh: Mesh; cx: number; cz: number }[] = [];
  private readonly smoke: { mesh: Mesh; phase: number; base: number; rise: number; size: number; period: number }[] = [];
  /** Gates, levers, crates, telescopes and tower ladder frames; game code drives them from state. */
  readonly fixtures: IslandFixtures;
  private readonly gulls: { root: TransformNode; wings: Mesh[]; cx: number; cy: number; cz: number; radius: number; speed: number; phase: number }[] = [];
  private readonly lantern: LanternScene;
  /** One sky material per cloud-step variant (presets sharing a step count share it). */
  private readonly skyMaterials: Partial<Record<48 | 96, ShaderMaterial>> = {};
  private readonly cameraEffects = new Map<Camera, { pipeline: DefaultRenderingPipeline; contacts: ContactShadows | null } | null>();
  /** Layer that meshes created during construction belong to; meshes join it when `building` next changes. */
  private layer: EnvironmentLayer = "structures";
  /** Meshes the scene held before construction: not the environment's. Emptied once built. */
  private readonly foreignMeshes = new Set<AbstractMesh>();
  private readonly layerMeshes = new Map<AbstractMesh, EnvironmentLayer>();
  /** Meshes each hidden layer switched off, so showing it restores only those. */
  private readonly hiddenLayers = new Map<EnvironmentLayer, AbstractMesh[]>();

  constructor(private readonly scene: Scene, private preset: GraphicsPreset = "balanced",
    private contactSamples: ContactShadowSamples = 48) {
    for (const mesh of scene.meshes) this.foreignMeshes.add(mesh);
    this.mats = new Materials(scene);
    scene.clearColor = new Color4(FOG_DAY.r, FOG_DAY.g, FOG_DAY.b, 1);
    scene.ambientColor = Color3.Black(); // The sky IBL supplies ambient light without washing out wood.
    scene.fogMode = 2; // FOGMODE_EXP2
    scene.fogColor = FOG_DAY.clone();
    scene.fogDensity = FOG_DENSITY;

    const ip = scene.imageProcessingConfiguration;
    ip.toneMappingEnabled = true;
    ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
    ip.exposure = 1.0;
    ip.contrast = 1.08;
    ip.colorCurvesEnabled = true;
    ip.colorCurves = new ColorCurves();
    ip.colorCurves.globalSaturation = 8;
    ip.vignetteEnabled = true;
    ip.vignetteWeight = 0.65;
    ip.vignetteColor = new Color4(0, 0, 0, 0);
    // One output-code-value dither smooths the sky/fog gradient without film grain.
    ip.ditheringEnabled = true;
    ip.ditheringIntensity = 1 / 255;

    const hemi = new HemisphericLight("hemi", new Vector3(0.2, 1, 0.1), scene);
    hemi.intensity = DAY_HEMI_INTENSITY;
    hemi.diffuse = new Color3(0.95, 0.97, 1.0);
    hemi.groundColor = new Color3(0.35, 0.45, 0.55);
    this.sun = new DirectionalLight("sun", DAY_SUN_DIRECTION.clone(), scene);
    this.sun.intensity = DAY_SUN_INTENSITY;
    this.sun.diffuse = new Color3(1.0, 0.91, 0.72);
    // Both presets retain four stabilized cascades and the same island-range coverage.
    // A null generator camera follows scene.activeCamera without recreating the caster list.
    this.shadow = new CascadedShadowGenerator(GRAPHICS_PRESETS[preset].shadowMapSize, this.sun);
    this.shadow.numCascades = 4;
    this.shadow.shadowMaxZ = 650;
    this.shadow.lambda = 0.88;
    this.shadow.stabilizeCascades = true;
    this.shadow.autoCalcDepthBounds = false;
    this.shadow.cascadeBlendPercentage = 0.1;
    this.shadow.usePercentageCloserFiltering = true;
    this.shadow.filteringQuality = CascadedShadowGenerator.QUALITY_HIGH;
    this.shadow.bias = 0.0005;
    this.shadow.normalBias = 0.02;
    this.shadowCulling = new CascadeShadowCulling(scene, this.shadow);
    this.shadowCulling.bind();

    this.shore = bakeShoreMap(scene);
    this.building = "sky";
    this.buildSky();
    setupDaylightIBL(scene, SKY_ZENITH, FOG_DAY, this.sun.direction);
    this.building = "water";
    this.buildWater();
    this.oceanCaptures = new OceanCaptures(scene, this.water, this.waterMat, this.skyDome, {
      resolutionScale: GRAPHICS_PRESETS[preset].captureScale, enabled: GRAPHICS_PRESETS[preset].captures,
    });
    for (const l of LANDMASSES) this.buildLandmass(l);
    this.building = "structures";
    for (const outpost of OUTPOSTS) this.buildOutpost(outpost);
    this.buildRocks();
    this.building = "structures";
    this.buildSupplyBarrels();
    this.lantern = new LanternScene(scene, this.mats, mesh => this.addCaster(mesh),
      (x, z) => terrainSurfaceHeight(LANTERN_ISLE, x, z));
    new SkullScene(scene, this.mats, mesh => this.addCaster(mesh));
    // Batching reads the layer of every scenery prototype and mesh built so far.
    this.tagMeshes();
    this.buildSceneryBatches();
    this.mergeStatics();
    this.building = "vegetation";
    this.buildTufts();
    this.buildFortressLights();
    this.building = "weather";
    this.weather = new Weather(scene);
    this.building = "structures";
    this.fixtures = new IslandFixtures(scene, this.mats, (mesh) => this.sceneryLighting(mesh));
    this.tagMeshes();
    this.foreignMeshes.clear();
    for (const mesh of this.layerMeshes.keys()) if (mesh.isDisposed()) this.layerMeshes.delete(mesh);
    const engine = scene.getEngine();
    const restored = engine.onContextRestoredObservable.add(() => this.applyGraphicsPreset());
    scene.onDisposeObservable.addOnce(() => {
      engine.onContextRestoredObservable.remove(restored);
      this.shadowCulling.dispose();
    });
  }

  /**
   * Starts a construction layer after tagging the meshes built since the last one. Tagging scans
   * the scene: Babylon announces new meshes (`onNewMeshAddedObservable`) only on a later tick.
   */
  private set building(next: EnvironmentLayer) {
    this.tagMeshes();
    this.layer = next;
  }

  private tagMeshes(): void {
    for (const mesh of this.scene.meshes) if (!this.foreignMeshes.has(mesh) && !this.layerMeshes.has(mesh)) this.layerMeshes.set(mesh, this.layer);
  }

  /**
   * Water-safe contact shading, HDR bloom and filmic tone mapping on the main camera.
   * MSAA lives on the first scene-colour target. Capture cameras remain unprocessed.
   * Effects compiled on a lost context lose samplers in Babylon, so construction
   * waits for restoration and cancels that listener on scene disposal.
   */
  attachCamera(camera: Camera): void {
    this.scene.activeCamera = camera;
    if (this.cameraEffects.has(camera)) return;
    this.cameraEffects.set(camera, null);
    const engine = this.scene.getEngine();
    const create = (): void => {
      if (this.scene.isDisposed || camera.isDisposed()) return;
      const hdr = engine.getCaps().textureHalfFloatRender;
      const samples = Math.min(GRAPHICS_PRESETS[this.preset].samples, engine.getCaps().maxMSAASamples);
      const contacts = attachContactShadows(this.scene, camera, this.water, this.skyDome, samples, this.contactSamples);
      const pipeline = new DefaultRenderingPipeline(`post:${camera.uniqueId}`, hdr, this.scene, [camera], false);
      pipeline.imageProcessingEnabled = true;
      pipeline.samples = contacts ? 1 : samples;
      pipeline.fxaaEnabled = false;
      pipeline.bloomEnabled = hdr;
      pipeline.bloomThreshold = 1.4;
      pipeline.bloomWeight = 0.16;
      pipeline.bloomKernel = 64;
      // Babylon scales the blur kernel internally; retain its output-space radius.
      pipeline.bloomScale = 0.5;
      pipeline.sharpenEnabled = true;
      pipeline.sharpen.edgeAmount = 0.08;
      pipeline.sharpen.colorAmount = 1.0;
      pipeline.grainEnabled = false;
      pipeline.chromaticAberrationEnabled = false;
      pipeline.depthOfFieldEnabled = false;
      pipeline.automaticBuild = true;
      pipeline.prepare();
      this.cameraEffects.set(camera, { pipeline, contacts });
    };
    camera.onDisposeObservable.addOnce(() => {
      this.cameraEffects.get(camera)?.pipeline.dispose();
      this.cameraEffects.delete(camera);
    });
    if (!(engine instanceof ThinEngine && engine._gl.isContextLost())) {
      create();
      return;
    }
    const restored = engine.onContextRestoredObservable.addOnce(create);
    camera.onDisposeObservable.addOnce(() => engine.onContextRestoredObservable.remove(restored));
    this.scene.onDisposeObservable.addOnce(() => engine.onContextRestoredObservable.remove(restored));
  }

  /** Cost changes apply live without rebuilding the world or losing camera/game state. */
  setGraphicsPreset(preset: GraphicsPreset): void {
    if (preset === this.preset) return;
    this.preset = preset;
    const engine = this.scene.getEngine();
    if (!(engine instanceof ThinEngine && engine._gl.isContextLost())) this.applyGraphicsPreset();
  }

  setContactShadowSamples(samples: ContactShadowSamples): void {
    if (samples === this.contactSamples) return;
    this.contactSamples = samples;
    const engine = this.scene.getEngine();
    if (engine instanceof ThinEngine && engine._gl.isContextLost()) return;
    for (const effects of this.cameraEffects.values()) effects?.contacts?.setSamples(samples);
  }

  /** Shows or hides one subsystem; hidden layers also skip their per-frame update and captures. */
  setLayerVisible(layer: EnvironmentLayer, visible: boolean): void {
    const hidden = this.hiddenLayers.get(layer);
    if (visible) {
      for (const mesh of hidden ?? []) mesh.isVisible = true;
      this.hiddenLayers.delete(layer);
      return;
    }
    if (hidden) return;
    const meshes: AbstractMesh[] = [];
    for (const [mesh, owner] of this.layerMeshes) {
      if (owner !== layer || mesh.isDisposed() || !mesh.isVisible) continue;
      mesh.isVisible = false;
      meshes.push(mesh);
    }
    this.hiddenLayers.set(layer, meshes);
  }

  private applyGraphicsPreset(): void {
    if (this.scene.isDisposed) return;
    const quality = GRAPHICS_PRESETS[this.preset];
    this.shadow.mapSize = quality.shadowMapSize;
    this.shadowCulling.bind();
    this.oceanCaptures.configure({ resolutionScale: quality.captureScale, enabled: quality.captures });
    this.applyOceanQuality();
    this.skyMat = this.skyMaterial(this.preset);
    this.skyDome.material = this.skyMat;
    const samples = Math.min(quality.samples, this.scene.getEngine().getCaps().maxMSAASamples);
    for (const effects of this.cameraEffects.values()) {
      if (!effects) continue;
      if (effects.contacts) {
        effects.contacts.input.samples = samples;
        effects.contacts.setSamples(this.contactSamples);
      } else effects.pipeline.samples = samples;
    }
  }

  /** Preserve UVs and culling locality; never merge animated descendants or prototypes. */
  private mergeStatics(): void {
    const animated = new Set<TransformNode>(this.windmillBlades);
    const casters = new Set(this.casters);
    const groups = new Map<string, { meshes: Mesh[]; casts: boolean; layer: EnvironmentLayer }>();
    for (const [mesh, region] of this.regionalMeshes) {
      if (!mesh.material || !mesh.isEnabled() || mesh.instances.length || mesh.hasThinInstances || mesh.getTotalVertices() === 0) continue;
      if (mesh.material instanceof PBRMaterial && (mesh.material.unlit || mesh.material.alpha < 1)) continue;
      let moving = false;
      for (let p = mesh.parent; p; p = p.parent) if (p instanceof TransformNode && animated.has(p)) moving = true;
      if (moving) continue;
      const casts = casters.has(mesh);
      const layer = this.layerMeshes.get(mesh) ?? "structures";
      const key = `${region}:${layer}:${mesh.material.uniqueId}:${casts}`;
      let batch = groups.get(key);
      if (!batch) groups.set(key, (batch = { meshes: [], casts, layer }));
      batch.meshes.push(mesh);
    }
    for (const [key, batch] of groups) {
      if (batch.meshes.length === 1) { batch.meshes[0]!.freezeWorldMatrix(); continue; }
      for (const mesh of batch.meshes) if (batch.casts) this.removeCaster(mesh);
      const merged = Mesh.MergeMeshes(batch.meshes, true, true);
      if (!merged) throw new Error(`Cannot merge scenery region ${key}`);
      merged.name = `scenery:${key}`;
      merged.isPickable = false;
      merged.receiveShadows = true;
      merged.freezeWorldMatrix();
      if (batch.casts) this.addCaster(merged);
      this.layerMeshes.set(merged, batch.layer);
    }
    this.regionalMeshes.clear();
  }

  /** Registers a mesh as a shadow caster. */
  addCaster(mesh: AbstractMesh): void {
    this.casters.push(mesh);
    this.shadow.addShadowCaster(mesh, false);
  }

  removeCaster(mesh: AbstractMesh): void {
    const i = this.casters.indexOf(mesh);
    if (i >= 0) this.casters.splice(i, 1);
    this.shadow.removeShadowCaster(mesh, false);
  }

  setLanternState(state: LanternVisualState): void {
    this.lantern.setState(state);
  }

  update(dt: number, camera: Camera, reducedMotion: boolean): void {
    this.time += dt;
    if (!reducedMotion) this.visualTime += dt;
    const cp = camera.globalPosition;
    // Keep the water's LOD rings under the camera, snapped to the coarsest spacing so no level's vertices swim.
    this.water.position.x = Math.round(cp.x / OCEAN_SNAP) * OCEAN_SNAP;
    this.water.position.z = Math.round(cp.z / OCEAN_SNAP) * OCEAN_SNAP;
    this.waterMat.setFloat("time", this.visualTime);
    this.waterMat.setFloat("waveScale", reducedMotion ? REDUCED_MOTION_WAVES : 1);
    this.skyMat.setFloat("time", this.visualTime);
    this.waterMat.setVector3("cameraPosition", cp);
    // Below the local wave surface, not the mean sea level, so a swimmer in a trough stays above water.
    const under = cp.y < this.seaHeight(cp.x, cp.z, 0, reducedMotion) - 0.05;
    // Hidden weather keeps its fog and light blend but skips precipitation.
    this.weather.update(dt, cp, reducedMotion || this.hiddenLayers.has("weather"), under);
    this.underwater = under;
    this.scene.fogColor.copyFrom(under ? FOG_UNDER : this.weather.fog);
    this.scene.fogDensity = under ? 0.06 : FOG_DENSITY * this.weather.densityMul;
    const fog = this.scene.fogColor;
    this.scene.clearColor.set(fog.r, fog.g, fog.b, 1);
    this.skyDome.setEnabled(!under);
    this.skyMat.setColor3("horizon", under ? FOG_DAY : fog);
    this.skyMat.setColor3("zenith", this.weather.zenith);
    this.skyMat.setFloat("storm", this.weather.storm);
    this.waterMat.setColor3("horizon", fog);
    this.waterMat.setColor3("zenith", this.weather.zenith);
    this.sun.intensity = DAY_SUN_INTENSITY * this.weather.sunMul;
    for (const terrain of this.terrainMeshes) terrain.mesh.setEnabled(Math.hypot(cp.x - terrain.cx, cp.z - terrain.cz) <= 2400);
    const wildlife = !this.hiddenLayers.has("wildlife");
    if (wildlife) for (const puff of this.smoke) {
      const phase = (this.time / puff.period + puff.phase) % 1;
      puff.mesh.position.y = puff.base + phase * puff.rise;
      puff.mesh.scaling.setAll((0.8 + phase * 1.3) * puff.size);
      puff.mesh.visibility = reducedMotion ? 0 : Math.sin(phase * Math.PI) * 0.8;
    }
    this.waterMat.setColor3("fogColor", this.scene.fogColor);
    this.waterMat.setFloat("fogDensity", this.scene.fogDensity);
    // Hidden water also skips its reflection and refraction captures and the ship–sea simulation.
    if (!this.hiddenLayers.has("water")) this.updateSea(dt, cp, reducedMotion);
    this.oceanCaptures.update(this.hiddenLayers.has("water") ? null : camera, under);
    if (!this.hiddenLayers.has("structures")) for (const b of this.windmillBlades) b.rotation.z += dt * (reducedMotion ? 0.2 : 0.6);
    if (wildlife) for (const gull of this.gulls) {
      const a = gull.phase + this.time * gull.speed * (reducedMotion ? .3 : 1), dir = Math.sign(gull.speed);
      gull.root.position.set(gull.cx + Math.cos(a) * gull.radius, gull.cy + Math.sin(a * 2.3 + gull.phase) * 1.5, gull.cz + Math.sin(a) * gull.radius);
      // Heading along the circle's tangent, banked into the turn.
      gull.root.rotation.set(0, Math.atan2(-Math.sin(a) * dir, Math.cos(a) * dir), -.35 * dir);
      const flap = reducedMotion ? 0 : gullFlap(this.time, gull.phase);
      gull.wings[0]!.rotation.z = -flap;
      gull.wings[1]!.rotation.z = flap;
    }
    const pulse = 0.85 + Math.sin(this.time * 2) * 0.15;
    for (const m of this.lampMats) m.emissiveColor.set(pulse, pulse * 0.82, pulse * 0.4);
  }

  get isUnderwater(): boolean {
    return this.underwater;
  }

  /** Hulls in the water this frame (flat poses, speed, immersion); `update` simulates the nearest `SIM_HULLS`. */
  setHullSources(sources: readonly HullSource[]): void {
    this.hullSources = sources;
  }

  private updateSea(dt: number, camera: Vector3, reducedMotion: boolean): void {
    const near = [...this.hullSources].sort((a, b) => Math.hypot(a.x - camera.x, a.z - camera.z) - Math.hypot(b.x - camera.x, b.z - camera.z)).slice(0, SIM_HULLS);
    this.sim.update(dt, camera.x, camera.z, near, this.waveParams.wake);
    const [cx, cz, extent] = this.sim.window, [sx, sz] = this.sim.shift;
    // An idle sea has no hulls to throw spray, and its droplets have long since fallen.
    // Spray is decorative motion: reduced motion stops new droplets.
    if (this.sim.active) this.spray.update(dt, this.sim.surface, cx, cz, extent, sx, sz, near, reducedMotion ? 0 : this.waveParams.wake.spray, camera,
      0.25 + 0.75 * this.sun.intensity / DAY_SUN_INTENSITY);
    this.waterMat.setTexture("simMap", this.sim.display);
    this.waterMat.setVector4("simWindow", this.simWindow.set(cx, cz, extent, this.sim.active ? 1 : 0));
  }

  /** The live wave parameters (initially `GAME_WAVES`); change them with `setWaves`. */
  get waves(): Readonly<WaveParams> {
    return this.waveParams;
  }

  /** The components, bands and foam source the water renders (`waveSurface(waves)`). */
  get waveSurface(): Readonly<WaveSurface> {
    return this.surface;
  }

  /** Replaces the wave parameters for the water shader and for `shipMotion`. */
  setWaves(params: Readonly<WaveParams>): void {
    if (params.sines.swells.length !== 6 || params.ripples.length !== 3) throw new Error("Waves need six swells and three ripple layers");
    this.waveParams = cloneWaves(params);
    this.surface = waveSurface(this.waveParams);
    // ShaderMaterial keeps vectors and arrays by reference: every upload needs its own instance.
    const m = this.waterMat, p = this.waveParams, s = this.surface;
    this.uploadComponents();
    m.setFloat("waveCount", s.components.length);
    m.setFloat("foamSource", s.foamFromCompression ? 1 : 0);
    m.setFloat("farSlope", p.farSlope);
    p.ripples.forEach((r, i) => m.setVector3(`ripple${"ABC"[i]}`, new Vector3(r.scale, r.strength, r.speed)));
    m.setVector3("crestTint", new Vector3(s.crestTint.start, s.crestTint.end, s.crestTint.amount));
    m.setVector3("crestFoam", new Vector3(s.crestFoam.start, s.crestFoam.end, s.crestFoam.amount));
    const shore = p.shore;
    m.setVector4("shoreShape", new Vector4(shore.shoalDepth, shore.surfDepth, Math.max(shore.surfSpacing, 0.05), Math.max(shore.surfPeriod, 0.1)));
    m.setVector4("shoreLook", new Vector4(shore.surfHeight, shore.breakerFoam, shore.washBase, shore.washRange));
  }

  /** Writes the components into the water's wave arrays (kept by reference, so rewrites reach the shader). */
  private uploadComponents(): void {
    const k = this.waveK.fill(0), a = this.waveA.fill(0);
    this.surface.components.forEach((c, i) => {
      k[i * 4] = c.kx; k[i * 4 + 1] = c.kz; k[i * 4 + 2] = c.omega; k[i * 4 + 3] = c.phase;
      a[i * 4] = c.amplitude; a[i * 4 + 1] = c.lateral; a[i * 4 + 2] = c.mesh ? 1 : 0; a[i * 4 + 3] = 1 / Math.hypot(c.kx, c.kz);
    });
    this.waterMat.setArray4("waveK", k);
    this.waterMat.setArray4("waveA", a);
    this.waterMat.setFloat("waveBand", this.surface.bandScale);
  }

  /**
   * The room's weather: the wind reweights the Gerstner components in place (the sea changes
   * smoothly; a new prevailing heading rebuilds them once), and storminess darkens the sky.
   */
  setWeather(weather: Readonly<WeatherSample>): void {
    const g = this.waveParams.gerstner;
    if (g.prevailingHeading !== weather.prevailingHeading) {
      this.setWaves({ ...this.waveParams, gerstner: { ...g, prevailingHeading: weather.prevailingHeading, windSpeed: weather.windSpeed, windHeading: weather.windHeading } });
    } else if (g.windSpeed !== weather.windSpeed || g.windHeading !== weather.windHeading) {
      g.windSpeed = weather.windSpeed;
      g.windHeading = weather.windHeading;
      blowWind(this.surface, g);
      this.uploadComponents();
    }
    this.weather.storm = weather.storm;
  }

  /** Shader wave time: advances with `update`'s `dt` except under reduced motion. */
  get waveTime(): number {
    return this.visualTime;
  }

  /**
   * How a hull at (x, z) facing `heading` rides the waves that the next `update(dt, …, reducedMotion)`
   * renders. Reduced motion keeps hulls level (no decorative camera movement for passengers).
   */
  shipMotion(x: number, z: number, heading: number, dt: number, reducedMotion: boolean, out: ShipMotion): ShipMotion {
    if (reducedMotion) {
      out.heave = out.pitch = out.roll = 0;
      return out;
    }
    return shipMotion(this.surface.components, 1, this.visualTime + dt, x, z, heading, out, this.waveParams.shore);
  }

  /**
   * Height of the rendered sea mesh at (x, z) for the next `update(dt, …, reducedMotion)`, so
   * swimmers ride the waves they are drawn in. Reduced motion renders frozen, lower waves.
   */
  seaHeight(x: number, z: number, dt: number, reducedMotion: boolean): number {
    return waterHeight(this.surface.components, this.waveParams.shore, reducedMotion ? REDUCED_MOTION_WAVES : 1,
      x, z, reducedMotion ? this.visualTime : this.visualTime + dt, true);
  }

  private buildSky(): void {
    this.skyDome = CreateSphere("sky", { diameter: 5000, segments: 16, sideOrientation: Mesh.BACKSIDE }, this.scene);
    this.skyMat = this.skyMaterial(this.preset);
    this.skyDome.material = this.skyMat;
    this.skyDome.infiniteDistance = true;
    this.skyDome.isPickable = false;
  }

  private skyMaterial(preset: GraphicsPreset): ShaderMaterial {
    const steps = GRAPHICS_PRESETS[preset].cloudSteps;
    let material = this.skyMaterials[steps];
    if (material) return material;
    material = new ShaderMaterial(
      `sky:${steps}`,
      this.scene,
      { vertexSource: SKY_VS, fragmentSource: skyFragmentShader(steps) },
      { attributes: ["position"], uniforms: ["world", "viewProjection", "zenith", "horizon", "sunDir", "time", "storm"] },
    );
    material.backFaceCulling = false;
    material.setColor3("zenith", SKY_ZENITH);
    material.setColor3("horizon", this.scene.fogColor);
    material.setVector3("sunDir", this.sun.direction.scale(-1).normalize());
    material.setFloat("time", this.visualTime);
    // The sky is built before `weather`; `update` sets the room's storminess every frame.
    material.setFloat("storm", 0);
    this.skyMaterials[steps] = material;
    return material;
  }

  private buildWater(): void {
    this.water = new Mesh("water", this.scene);
    const src = waterShaders(LANDMASSES.length, ROCKS.length, this.shore.tiles);
    this.waterMat = new ShaderMaterial(
      "waterMat",
      this.scene,
      { vertexSource: src.vertex, fragmentSource: src.fragment },
      {
        attributes: ["position"],
        uniforms: ["world", "viewProjection", "time", "waveScale", "cameraPosition", "fogColor", "fogDensity", "sunDir", "zenith", "horizon", "shoreRects", "rocks",
          "reflectionViewProjection", "refractionViewProjection", "inverseReflectionViewProjection", "inverseRefractionViewProjection", "captureTexel", "captureDepthAvailable", "captureEnabled", "captureDepthNdc", "captureClearDepth",
          "waveK", "waveA", "waveCount", "waveNormals", "waveBand", "foamSource", "farSlope", "rippleA", "rippleB", "rippleC", "crestTint", "crestFoam",
          "shoreShape", "shoreLook", "simWindow", "oceanLod"],
        samplers: ["shoreMap", "reflectionSampler", "refractionSampler", "reflectionDepthSampler", "refractionDepthSampler", "environmentSampler", "simMap"],
        needAlphaBlending: false,
      },
    );
    this.waterMat.backFaceCulling = false;
    this.sim = new WaterSim(this.scene);
    this.spray = new SeaSpray(this.scene);
    this.scene.onDisposeObservable.addOnce(() => { this.sim.dispose(); this.spray.dispose(); });
    this.waterMat.setTexture("simMap", this.sim.display);
    this.waterMat.setVector4("simWindow", this.simWindow);
    this.setWaves(this.waveParams);
    this.applyOceanQuality();
    this.waterMat.setVector3("sunDir", this.sun.direction.scale(-1).normalize());
    this.waterMat.setColor3("zenith", SKY_ZENITH);
    this.waterMat.setColor3("horizon", FOG_DAY);
    this.waterMat.setTexture("shoreMap", this.shore.texture);
    this.waterMat.setArray4("shoreRects", this.shore.rects);
    this.waterMat.setArray3("rocks", ROCKS.flatMap((r) => [r.x, r.z, r.r * 0.8]));
    this.water.material = this.waterMat;
    this.water.isPickable = false;
    this.water.alphaIndex = 10;
  }

  /** The preset's ocean LOD mesh, fragment wave components and wake-simulation step rate. */
  private applyOceanQuality(): void {
    const ocean = GRAPHICS_PRESETS[this.preset].ocean;
    const lod = oceanMesh(ocean);
    const data = new VertexData();
    data.positions = lod.positions;
    data.indices = lod.indices;
    data.applyToMesh(this.water);
    this.waterMat.setArray4("oceanLod", lod.lod);
    this.waterMat.setFloat("waveNormals", ocean.normals);
    this.sim.stepEvery = ocean.wakeEvery;
  }

  private buildLandmass(l: Landmass): void {
    const extent = l.radius * 1.75;
    const skull = l.cx === SKULL_ORIGIN.x && l.cz === SKULL_ORIGIN.z;
    const step = terrainStep(l);
    const n = Math.ceil((extent * 2) / step);
    const positions: number[] = [];
    const weights: number[] = [];
    const macro: number[] = [];
    const indices: number[] = [];
    const theme = TERRAIN[l.theme];
    const clear = dressingClearance(l);
    const tuftGroups = [0, 1].map(variant => {
      const batch = { land: l, variant, matrices: [] as number[] };
      this.tuftBatches.set(`${l.id}:${variant}`, batch);
      return batch;
    });
    const tuftScale = new Vector3();
    const tuftPos = new Vector3();
    const tuftRot = new Quaternion();
    const tuftMatrix = new Matrix();
    for (let iz = 0; iz <= n; iz++) {
      for (let ix = 0; ix <= n; ix++) {
        const x = l.cx - extent + ix * step;
        const z = l.cz - extent + iz * step;
        const h = terrainHeight(x, z);
        positions.push(x, h, z);
        const slope = Math.abs(terrainHeight(x + 1, z) - h) + Math.abs(terrainHeight(x, z + 1) - h);
        // Trampled yards and worn footpaths keep only patchy grass; Skull's paths wear by its own map.
        const trodden = !skull && isTrodden(l, x - l.cx, z - l.cz, .5 * valueNoise(x * .4, z * .4, l.seed + 11));
        let grass = smoothstep(1.1, 1.6, h) * (trodden ? .08 + .4 * smoothstep(.15, .65, valueNoise(x * .12, z * .12, l.seed + 9)) : 1);
        if (skull && grass > 0) grass *= 1 - skullGroundWear(x, z);
        // Cliff-walled islands are grassy on top; only their walls turn to rock.
        const rock = skull ? Math.max(smoothstep(0.65, 1.65, slope), skullCliffRockWeight(x - SKULL_ORIGIN.x, z - SKULL_ORIGIN.z))
          : Math.max(smoothstep(0.8, 1.3, slope), l.mesas ? 0 : smoothstep(10.5, 11.5, h));
        weights.push((1 - rock) * (1 - grass), (1 - rock) * grass, rock);
        macro.push(clamp((h - 1.25) / 8, 0, 1), 0.5 * (0.5 + 0.5 * valueNoise(x * 0.08, z * 0.08, l.seed + 7)));
        if (!skull && theme.tufts && h >= 1.6 && (l.mesas || h <= 10) && slope < 0.7 && hash2(ix, iz, l.seed + 101) < (trodden ? .04 : .25)) {
          const jx = x + (hash2(ix, iz, l.seed + 102) - 0.5) * step;
          const jz = z + (hash2(ix, iz, l.seed + 103) - 0.5) * step;
          if (!clear(jx, jz)) continue;
          tuftPos.set(jx, terrainSurfaceHeight(l, jx, jz) - 0.03, jz);
          Quaternion.RotationYawPitchRollToRef(hash2(ix, iz, l.seed + 104) * TAU, 0, 0, tuftRot);
          const s = 0.7 + 0.6 * hash2(ix, iz, l.seed + 105);
          tuftScale.set(s, s, s);
          Matrix.ComposeToRef(tuftScale, tuftRot, tuftPos, tuftMatrix);
          const batch = tuftGroups[hash2(ix, iz, l.seed + 106) < 0.5 ? 0 : 1]!;
          for (const v of tuftMatrix.m) batch.matrices.push(v);
        }
      }
    }
    for (let iz = 0; iz < n; iz++) {
      for (let ix = 0; ix < n; ix++) {
        const a = iz * (n + 1) + ix;
        const b = a + 1;
        const c = a + n + 1;
        const d = c + 1;
        // Skip quads entirely on the deep sea floor.
        if (positions[a * 3 + 1]! < -15 && positions[b * 3 + 1]! < -15 && positions[c * 3 + 1]! < -15 && positions[d * 3 + 1]! < -15) continue;
        indices.push(a, b, c, b, d, c);
      }
    }
    this.building = "terrain";
    const mesh = new Mesh(`land-${l.id}`, this.scene);
    const vd = new VertexData();
    vd.positions = positions;
    vd.indices = indices;
    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);
    vd.normals = normals;
    vd.applyToMesh(mesh);
    mesh.setVerticesData("terrainWeights", weights, false, 3);
    mesh.setVerticesData("terrainMacro", macro, false, 2);
    mesh.material = createTerrainMaterial(this.scene, this.mats, l.theme, skull);
    mesh.receiveShadows = true;
    mesh.isPickable = false;
    mesh.freezeWorldMatrix();
    this.addCaster(mesh);
    this.terrainMeshes.push({ mesh, cx: l.cx, cz: l.cz });
    // Skull owns its canopy, fort silhouette and clustered dressing, not generic ruins.
    if (skull) return;

    this.building = "vegetation";
    for (const p of l.trees) {
      const seed = hash2(p.x, p.z, l.seed + 201);
      const name = l.theme === "frost" ? "pine" : l.theme === "marsh" ? "marshTree" : seed < 0.5 ? "palm" : "palmLeaning";
      const x = l.cx + p.x, z = l.cz + p.z;
      this.placeScenery(name, l.theme, x, terrainHeight(x, z), z, l.id, true, seed * TAU);
    }
    for (const m of l.landmarks) {
      this.building = "structures";
      this.landmark(l, m);
    }
    this.buildIslandStructures(l);
    this.building = "vegetation";
    this.buildGroundAccents(l, clear);
  }

  private buildFortressLights(): void {
    const meshes = this.scene.meshes.filter(mesh => mesh.name.startsWith("skull:") || mesh.name === `land-${SKULL_ISLE.id}`);
    for (const mesh of meshes) if (mesh.material instanceof PBRMaterial) mesh.material.maxSimultaneousLights = 2 + SKULL_LIGHT_ANCHORS.length;
    for (const anchor of SKULL_LIGHT_ANCHORS) {
      const light = new PointLight(`fort-lantern:${anchor.id}`, new Vector3(anchor.x, anchor.y, anchor.z), this.scene);
      light.diffuse = Color3.FromHexString("#ffd28a").toLinearSpace();
      light.intensity = 16;
      light.range = 7;
      light.radius = 0.12;
      light.includedOnlyMeshes = meshes.filter(mesh => {
        const box = mesh.getBoundingInfo().boundingBox;
        return Math.hypot(Math.max(box.minimumWorld.x - anchor.x, 0, anchor.x - box.maximumWorld.x),
          Math.max(box.minimumWorld.y - anchor.y, 0, anchor.y - box.maximumWorld.y),
          Math.max(box.minimumWorld.z - anchor.z, 0, anchor.z - box.maximumWorld.z)) < light.range;
      });
    }
  }

  /** Regional thin-instance buffers keep grass out of distant islands' bounds. */
  private buildTufts(): void {
    const material = this.mats.vertexColored("leaf").clone("grass-blades");
    material.backFaceCulling = false;
    for (const batch of this.tuftBatches.values()) {
      if (!batch.matrices.length) continue;
      const mesh = bladeClump(`tufts:${batch.land.id}:${batch.variant}`, batch.land.theme, batch.variant, this.scene);
      mesh.material = material;
      mesh.receiveShadows = true;
      mesh.isPickable = false;
      mesh.thinInstanceSetBuffer("matrix", new Float32Array(batch.matrices), 16, true);
      mesh.thinInstanceRefreshBoundingInfo(false);
    }
    this.tuftBatches.clear();
  }

  private placeScenery(name: SceneryName, theme: IslandTheme, x: number, y: number, z: number, region: string, repeated = false, yaw = 0): TransformNode {
    let root: TransformNode;
    if (repeated) {
      const key = `${name}:${theme}`;
      let prototype = this.sceneryPrototypes.get(key);
      if (!prototype) {
        prototype = spawnScenery(this.scene, this.mats, name, theme);
        for (const source of prototype.meshes) source.receiveShadows = !(source.material instanceof PBRMaterial && source.material.unlit);
        prototype.root.setEnabled(false);
        this.sceneryPrototypes.set(key, prototype);
      }
      root = new TransformNode(`${region}:${name}`, this.scene);
      root.position.set(x, y, z);
      root.rotation.y = yaw;
      for (const source of prototype.meshes) {
        const instance = source.createInstance(`${region}:${source.name}`);
        instance.parent = root;
        instance.setEnabled(true);
        instance.isPickable = false;
        instance.freezeWorldMatrix();
        if (this.canBatchScenery(source)) {
          // Capture Babylon's exact existing instance transform, including pivots.
          // In particular, createInstance reparents the source mesh directly to root;
          // composing the prototype ancestor hierarchy here would change placement.
          const batchKey = source.uniqueId;
          let batch = this.sceneryBatches.get(batchKey);
          if (!batch) this.sceneryBatches.set(batchKey, (batch = { source, matrices: [] }));
          for (const value of instance.getWorldMatrix().m) batch.matrices.push(value);
          instance.dispose();
        } else {
          this.sceneryLighting(instance);
        }
      }
    } else {
      const model = spawnScenery(this.scene, this.mats, name, theme);
      root = model.root;
      root.name = `${region}:${name}`;
      root.position.set(x, y, z);
      root.rotation.y = yaw;
      if (name === "windmill") this.windmillBlades.push(model.part("blades"));
      for (const mesh of model.meshes) {
        this.sceneryLighting(mesh);
        this.regionalMeshes.set(mesh, region);
      }
    }
    return root;
  }

  private canBatchScenery(source: Mesh): boolean {
    if (!source.material || source.skeleton || source.morphTargetManager || source.bakedVertexAnimationManager ||
      source.billboardMode || source.infiniteDistance || source.animations.length || source.getLODLevels().length ||
      source.hasThinInstances || source.visibility < 1) return false;
    for (let node = source.parent; node; node = node.parent) if (node.animations.length) return false;
    return source.subMeshes.every(subMesh => {
      const material = subMesh.getMaterial();
      return material !== null && !material.needAlphaBlendingForMesh(source);
    });
  }

  /** One immutable batch per prototype part: cascades render every shadow-list batch. */
  private buildSceneryBatches(): void {
    for (const [key, batch] of this.sceneryBatches) {
      const mesh = staticSceneryBatch(batch.source, `scenery-instances:${key}`, batch.matrices);
      this.layerMeshes.set(mesh, this.layerMeshes.get(batch.source) ?? "structures");
      this.sceneryLighting(mesh);
    }
    this.sceneryBatches.clear();
    // Unsupported ordinary instances still need their disabled source meshes.
    for (const prototype of this.sceneryPrototypes.values()) {
      if (prototype.meshes.every(mesh => mesh.instances.length === 0)) prototype.root.dispose();
    }
    this.sceneryPrototypes.clear();
  }

  private sceneryLighting(mesh: AbstractMesh): void {
    mesh.isPickable = false;
    if (mesh instanceof Mesh) mesh.receiveShadows = true;
    const material = mesh.material;
    if (material instanceof PBRMaterial && material.unlit) {
      if (mesh instanceof Mesh) mesh.receiveShadows = false;
      if (material.metadata?.surfaceFamily === "lamp" && !this.lampMats.includes(material)) this.lampMats.push(material);
      return;
    }
    this.addCaster(mesh);
  }

  private landmark(l: Landmass, landmark: Landmark): void {
    if (l.cx === LANTERN_ORIGIN.x && l.cz === LANTERN_ORIGIN.z) return;
    const x = l.cx + landmark.x, z = l.cz + landmark.z;
    if (landmark.kind === "volcano") {
      this.buildVolcano(l, x, z);
    } else if (landmark.kind !== "palmGrove" && landmark.kind !== "pineGrove" && landmark.kind !== "twinPeaks" && landmark.kind !== "stockadeGate") {
      const name = landmark.kind === "hut" && l.theme === "frost" ? "hutFrost" : landmark.kind;
      const y = terrainHeight(x, z);
      this.placeScenery(name, l.theme, x, y, z, l.id, false, landmark.yaw ?? 0);
      if (landmark.kind === "beaconTower") this.buildBeacon(x, y + BEACON_FLAME_Y, z);
    }
  }

  /** Smoke from the beacon basket and gulls wheeling around it. */
  private buildBeacon(x: number, y: number, z: number): void {
    this.building = "wildlife";
    const root = new TransformNode("beacon-smoke-root", this.scene);
    root.position.set(x, y, z);
    const material = this.mats.color("beacon-smoke", "#5d5853", 0, 0.5);
    material.opacityTexture = getSmokeTexture(this.scene);
    material.backFaceCulling = false;
    material.disableDepthWrite = true;
    for (let i = 0; i < 4; i++) {
      const puff = CreatePlane("beacon-smoke", { size: 6 }, this.scene);
      puff.material = material;
      puff.parent = root;
      puff.billboardMode = Mesh.BILLBOARDMODE_ALL;
      puff.isPickable = false;
      this.smoke.push({ mesh: puff, phase: i / 4, base: .8, rise: 16, size: .42, period: 4.5 });
    }
    // Wings carry both windings, so the shared vertex-coloured cloth surface suffices.
    const plumage = this.mats.vertexColored("fabric");
    for (let i = 0; i < 7; i++) {
      const gull = new TransformNode(`gull:${i}`, this.scene);
      const wings = [-1, 1].map(side => {
        const wing = gullWing(this.scene, side);
        wing.material = plumage;
        wing.parent = gull;
        wing.isPickable = false;
        return wing;
      });
      gull.scaling.setAll(1.1 + hash2(i, 1, 77) * .4);
      this.gulls.push({ root: gull, wings, cx: x + (hash2(i, 2, 77) - .5) * 14, cy: y + 4 + hash2(i, 3, 77) * 14, cz: z + (hash2(i, 4, 77) - .5) * 14,
        radius: 10 + hash2(i, 5, 77) * 26, speed: (hash2(i, 6, 77) < .3 ? -1 : 1) * (.18 + hash2(i, 7, 77) * .12), phase: i * 1.7 });
    }
  }

  private buildVolcano(l: Landmass, x: number, z: number): void {
    this.building = "landforms";
    const radius = l.radius * 0.1;
    const lava = new VertexData(), stain = new VertexData();
    const streams = [lava, stain].map(data => ({ data, positions: [] as number[], indices: [] as number[], uvs: [] as number[] }));
    type Point = { x: number; z: number; edge: number; heat: number; crust: number };
    const point = (px: number, pz: number): Point => {
      const r = Math.hypot(px, pz), seam = Math.abs(Math.sin(Math.atan2(pz, px) * 3 + r * 0.34));
      return { x: px, z: pz, edge: radius - r, heat: Math.max(radius * 0.3 - r, 0.13 - seam), crust: Math.max(radius * 0.4 - r, 0.3 - seam) };
    };
    // Clip triangles to the fissure contours, rather than painting whole square cells.
    const clip = (polygon: Point[], field: "edge" | "heat" | "crust", sign = 1): Point[] => {
      const result: Point[] = [];
      for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i]!, b = polygon[(i + 1) % polygon.length]!;
        const da = a[field] * sign, db = b[field] * sign;
        if ((da < 0 && db > 0) || (da > 0 && db < 0)) {
          const t = da / (da - db);
          result.push({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t,
            edge: a.edge + (b.edge - a.edge) * t, heat: a.heat + (b.heat - a.heat) * t,
            crust: a.crust + (b.crust - a.crust) * t });
        }
        if (db >= 0) result.push(b);
      }
      return result;
    };
    const step = 0.6;
    for (let dz = -radius; dz < radius; dz += step) for (let dx = -radius; dx < radius; dx += step) {
      const corners = [point(dx, dz), point(dx + step, dz), point(dx, dz + step), point(dx + step, dz + step)];
      for (const triangle of [[corners[0]!, corners[1]!, corners[2]!], [corners[1]!, corners[3]!, corners[2]!]]) {
        const inside = clip(triangle, "edge");
        for (let layer = 0; layer < 2; layer++) {
          const polygon = layer === 0 ? clip(inside, "heat") : clip(clip(inside, "crust"), "heat", -1);
          if (polygon.length < 3) continue;
          const stream = streams[layer]!, base = stream.positions.length / 3;
          for (const p of polygon) {
            const wx = x + p.x, wz = z + p.z;
            stream.positions.push(wx, terrainSurfaceHeight(l, wx, wz) + (layer === 0 ? 0.035 : 0.02), wz);
            stream.uvs.push(wx / SURFACE_PROFILES.rock.metres, wz / SURFACE_PROFILES.rock.metres);
          }
          for (let i = 1; i + 1 < polygon.length; i++) stream.indices.push(base, base + i, base + i + 1);
        }
      }
    }
    streams.forEach((stream, index) => {
      const mesh = new Mesh(index === 0 ? "volcano-lava" : "volcano-mineral", this.scene);
      stream.data.positions = stream.positions; stream.data.indices = stream.indices; stream.data.uvs = stream.uvs;
      const normals: number[] = [];
      VertexData.ComputeNormals(stream.positions, stream.indices, normals);
      stream.data.normals = normals; stream.data.applyToMesh(mesh);
      mesh.material = this.mats.get(index === 0 ? "lava" : "rockDark", index === 0 ? { emissive: 2 } : {});
      mesh.receiveShadows = index !== 0;
      mesh.isPickable = false;
      mesh.freezeWorldMatrix();
    });
    this.building = "wildlife";
    const root = new TransformNode("volcano-smoke-root", this.scene);
    root.position.set(x, terrainHeight(x, z), z);
    const material = this.mats.color("volcano-smoke", "#88827c", 0, 0.55);
    material.opacityTexture = getSmokeTexture(this.scene);
    material.backFaceCulling = false;
    material.disableDepthWrite = true;
    for (let i = 0; i < 3; i++) {
      const puff = CreatePlane("volcano-smoke", { size: 6 }, this.scene);
      puff.material = material;
      puff.parent = root;
      puff.billboardMode = Mesh.BILLBOARDMODE_ALL;
      puff.isPickable = false;
      this.smoke.push({ mesh: puff, phase: i / 3, base: 2.3, rise: 20, size: 1, period: 6 });
    }
  }

  private buildSupplyBarrels(): void {
    for (const barrel of SUPPLY_BARRELS) {
      const base = supplyBarrelWorld(barrel.id);
      const land = LANDMASSES.find(l => l.id === barrel.island)!;
      const root = this.placeScenery("supplyBarrel", land.theme, base.x, base.y, base.z, land.id, true);
      root.name = `supplyBarrel:${barrel.id}`;
    }
  }

  /** Cliff walls, stairs, jetties, boulders and authored props of hand-built islands. */
  private buildIslandStructures(l: Landmass): void {
    this.building = "landforms";
    const columns = cliffColumns(l);
    if (columns.length) {
      const { rock, dressing } = cliffMeshes(l, columns, this.scene);
      rock.material = this.mats.vertexColored("rock");
      this.staticSurface(rock);
      if (dressing.vines) {
        dressing.vines.material = this.mats.vertexColored("leaf");
        this.staticSurface(dressing.vines);
      }
      for (const s of dressing.stakes) this.placeScenery("stakesWall", l.theme, s.x, s.y, s.z, l.id, true, s.yaw);
    }
    if (l.boulders?.length) {
      const boulders = boulderMesh(l, l.boulders, terrainHeight, this.scene);
      boulders.material = this.mats.vertexColored("rock");
      this.staticSurface(boulders);
    }
    this.building = "structures";
    for (const s of l.stairs ?? []) this.buildStair(l, s);
    for (const d of l.docks ?? []) {
      this.pierDeck("jetty", l.id, l.cx + d.x0, l.cx + d.x1, l.cz + d.z0, l.cz + d.z1, d.top);
      for (const p of dockPiles(l, d)) this.placeScenery("pierPile", l.theme, p.x, d.top - 8.3, p.z, l.id, true);
      for (let z = d.z0 + 3; z < d.z1; z += 7) this.placeScenery("pierLantern", l.theme, l.cx + d.x0 + .2, d.top, l.cz + z, l.id, true);
    }
    for (const p of l.props ?? []) {
      // Animated, state-driven fixtures are owned by IslandFixtures.
      if (p.model === "gateLever" || p.model === "lootCrate" || p.model === "mountedTelescope") continue;
      const at = propPlacement(l, p);
      this.placeScenery(p.model, l.theme, at.x, at.y, at.z, l.id, true, at.yaw);
    }
  }

  /** Large single-mesh island geometry: lit, shadowed and frozen, never re-merged. */
  private staticSurface(mesh: Mesh): void {
    mesh.isPickable = false;
    mesh.receiveShadows = true;
    mesh.freezeWorldMatrix();
    this.addCaster(mesh);
  }

  /** Timber scaffold stair matching the shared tread colliders, with posts, braces and rails. */
  private buildStair(l: Landmass, s: Stair): void {
    const rise = (s.top - s.base) / s.steps, run = s.steps * s.tread;
    const ax = s.dirX, az = s.dirZ;
    // The open side is whichever side drops away from the cliff.
    const mx = l.cx + s.x + ax * run / 2, mz = l.cz + s.z + az * run / 2;
    const probe = s.width / 2 + 3;
    const side = terrainHeight(mx - az * probe, mz + ax * probe) < terrainHeight(mx + az * probe, mz - ax * probe) ? 1 : -1;
    const ox = -az * side, oz = ax * side;
    const at = (along: number, across: number, y: number) => new Vector3(l.cx + s.x + ax * along + ox * across, y, l.cz + s.z + az * along + oz * across);
    const half = s.width / 2;
    for (let i = 0; i <= s.steps; i++) {
      const a = i * s.tread, b = i === s.steps ? a + s.landing : a + s.tread;
      const y = Math.min(s.top, s.base + rise * (i + 1));
      const boards = i === s.steps ? Math.round(s.landing / .3) : 2;
      for (let k = 0; k < boards; k++) {
        // Boards run across the stair with a slight sag and the odd paler replacement plank.
        const mid = a + (b - a) * (k + .5) / boards, sag = (hash2(i, k, l.seed + 61) - .5) * .03;
        this.beam("stair-tread", hash2(i, k, l.seed + 67) < .25 ? "woodLight" : "plank", l.id,
          at(mid, -half + .02, y - .05 + sag), at(mid, half - .02, y - .05 - sag), (b - a) / boards - .025, .1);
      }
    }
    const top = s.top - .12;
    for (const across of [-half + .08, half - .08]) {
      this.beam("stair-stringer", "woodDark", l.id, at(0, across, s.base - .2), at(run, across, top), .14, .34);
      this.beam("landing-joist", "woodDark", l.id, at(run, across, top), at(run + s.landing, across, top), .14, .3);
    }
    // Posts on the open side carry the handrail; inner posts brace the stair against the rock.
    const posts: { along: number; y: number }[] = [];
    for (let along = .3; along < run + s.landing; along += 2.4) posts.push({ along, y: Math.min(s.top, s.base + rise * Math.ceil(along / s.tread)) });
    posts.push({ along: run + s.landing - .15, y: s.top });
    posts.forEach((p, i) => {
      for (const across of [half - .05, -half + .05]) {
        const g = at(p.along, across, 0);
        const foot = terrainHeight(g.x, g.z) - .3;
        const crown = across > 0 ? p.y + 1.05 : p.y - .1;
        if (crown - foot > .2) this.beam("stair-post", "wood", l.id, at(p.along, across, foot), at(p.along, across, crown), .2, .2);
      }
      const next = posts[i + 1];
      if (!next) return;
      const footA = terrainHeight(at(p.along, half, 0).x, at(p.along, half, 0).z), footB = terrainHeight(at(next.along, half, 0).x, at(next.along, half, 0).z);
      if (p.y - footA > 2) this.beam("stair-brace", "woodDark", l.id, at(p.along, half - .02, footA + .3), at(next.along, half - .02, next.y - .4), .12, .14);
      if (next.y - footB > 2) this.beam("stair-brace", "woodDark", l.id, at(next.along, half - .02, footB + .3), at(p.along, half - .02, p.y - .4), .12, .14);
    });
    for (const height of [1, .5]) {
      this.beam("stair-rail", "wood", l.id, at(0, half - .05, s.base + rise + height), at(run, half - .05, s.top + height), .08, .1);
      this.beam("landing-rail", "wood", l.id, at(run, half - .05, s.top + height), at(run + s.landing, half - .05, s.top + height), .08, .1);
    }
  }

  /** Static timber member from `from` to `to`, `width` across and `height` up when level. */
  private beam(name: string, slot: PaletteKey, region: string, from: Vector3, to: Vector3, width: number, height: number): void {
    const d = to.subtract(from), length = d.length();
    const mesh = CreateBox(name, { width, height, depth: length }, this.scene);
    mesh.position.copyFrom(from.add(to).scaleInPlace(.5));
    mesh.rotation.set(-Math.atan2(d.y, Math.hypot(d.x, d.z)), Math.atan2(d.x, d.z), 0);
    mesh.material = this.mats.get(slot);
    boxProjectUVs(mesh, SURFACE_PROFILES[slot === "plank" ? "planks" : "wood"].metres);
    this.sceneryLighting(mesh);
    this.regionalMeshes.set(mesh, region);
  }

  private buildOutpost(o: Outpost): void {
    const p = o.pier, theme = o.landmass.theme;
    this.pierDeck("pier", o.id, p.x0, p.x1, p.z0, p.z1, p.top);
    for (const g of o.gangways) this.pierDeck("gangway", o.id, g.minX, g.maxX, g.minZ, g.maxZ, p.top);
    for (let z = p.z0 + 4; z <= p.z1; z += 8) {
      for (const x of [p.x0 + PILE_INSET, p.x1 - PILE_INSET]) this.placeScenery("pierPile", theme, x, p.top - 8.3, z, o.id, true);
      const support = CreateBox("pier-support", { width: p.x1 - p.x0, height: 0.22, depth: 0.28 }, this.scene);
      support.position.set((p.x0 + p.x1) / 2, p.top - 0.51, z);
      support.material = this.mats.get("woodDark");
      boxProjectUVs(support, SURFACE_PROFILES.wood.metres);
      this.sceneryLighting(support); this.regionalMeshes.set(support, o.id);
      for (const x of [p.x0 + 0.25, p.x1 - 0.25]) {
        const bolt = CreateCylinder("pier-bolt", { diameter: 0.045, height: 0.016, tessellation: 6 }, this.scene);
        bolt.position.set(x, p.top + 0.003, z);
        bolt.material = this.mats.get("iron");
        cylinderUVs(bolt, 0.0225, 0.016, SURFACE_PROFILES.iron.metres);
        bolt.isPickable = false; bolt.receiveShadows = true;
        this.regionalMeshes.set(bolt, o.id);
      }
    }
    for (let z = p.z0 + 8; z <= p.z1; z += 16) this.placeScenery("pierLantern", theme, p.x0 + 0.2, p.top, z, o.id, true);
    this.placeScenery("questBoard", theme, o.questBoard.x, p.top, o.questBoard.z, o.id);
    this.placeScenery("merchantStall", theme, o.merchant.x, p.top, o.merchant.z, o.id);
    const landing = o.merchantLanding;
    this.pierDeck("merchantLanding", o.id, landing.minX, landing.maxX, landing.minZ, landing.maxZ, p.top);
    for (const z of [landing.minZ + 0.3, landing.maxZ - 0.3]) this.placeScenery("pierPile", theme, landing.minX + 0.3, p.top - 8.3, z, o.id, true);
    const merchant = spawnModel(this.scene, this.mats, "merchant");
    merchant.root.position.set(o.merchantStand.x, p.top, o.merchantStand.z);
    merchant.root.rotation.y = Math.PI / 2;
    for (const mesh of merchant.meshes) { this.sceneryLighting(mesh); this.regionalMeshes.set(mesh, o.id); }
    o.buildings.forEach((building, i) => {
      const cx = (building.minX + building.maxX) / 2;
      const cz = (building.minZ + building.maxZ) / 2;
      const seat = outpostBuildingSeat(building);
      this.placeScenery(i === 0 ? "tavern" : "shop", theme, cx, seat.floor, cz, o.id);
      const plinth = CreateBox("outpost-plinth", { width: building.maxX - building.minX, height: seat.top - seat.base, depth: building.maxZ - building.minZ }, this.scene);
      plinth.position.set(cx, (seat.top + seat.base) / 2, cz);
      plinth.material = this.mats.get("rock");
      boxProjectUVs(plinth, SURFACE_PROFILES.rock.metres);
      this.sceneryLighting(plinth);
      this.regionalMeshes.set(plinth, o.id);
    });
  }

  private pierDeck(name: string, region: string, x0: number, x1: number, z0: number, z1: number, top: number): void {
    const deck = bevelledSlab(name, x1 - x0, z1 - z0, 0.4, this.scene);
    deck.position.set((x0 + x1) / 2, top, (z0 + z1) / 2);
    deck.material = this.mats.get("plank");
    boxProjectUVs(deck, SURFACE_PROFILES.planks.metres);
    this.sceneryLighting(deck); this.regionalMeshes.set(deck, region);
  }

  private buildRocks(): void {
    this.building = "landforms";
    for (const r of ROCKS) {
      const rock = fittedRock("seaRock", r.r, r.h, this.scene);
      rock.material = this.mats.get("rockDark");
      rock.position.set(r.x, 0, r.z);
      rock.rotation.y = r.x;
      rock.freezeWorldMatrix();
      this.sceneryLighting(rock);
      this.regionalMeshes.set(rock, offshoreCell(r.x, r.z));
    }
    this.building = "structures";
    const half = WORLD.half;
    for (let s = -half; s <= half; s += 300) {
      this.placeScenery("buoy", "tropical", s, -0.5, -half, offshoreCell(s, -half), true);
      this.placeScenery("buoy", "tropical", s, -0.5, half, offshoreCell(s, half), true);
      if (s !== -half && s !== half) {
        this.placeScenery("buoy", "tropical", -half, -0.5, s, offshoreCell(-half, s), true);
        this.placeScenery("buoy", "tropical", half, -0.5, s, offshoreCell(half, s), true);
      }
    }
  }

  private buildGroundAccents(l: Landmass, clear: (x: number, z: number) => boolean): void {
    if (l.kind !== "island") return;
    const matrices: number[] = [];
    const rotation = new Quaternion(), position = new Vector3(), scale = new Vector3(), matrix = new Matrix();
    for (let i = 0; i < 960 && matrices.length / 16 < 240; i++) {
      const angle = hash2(i, 0, l.seed + 701) * TAU, radius = Math.sqrt(hash2(i, 1, l.seed + 701)) * l.radius;
      const x = l.cx + Math.sin(angle) * radius, z = l.cz + Math.cos(angle) * radius, h = terrainHeight(x, z);
      const slope = Math.abs(terrainHeight(x + 1, z) - h) + Math.abs(terrainHeight(x, z + 1) - h);
      if (h < 0.6 || slope > 0.7 || !clear(x, z)) continue;
      position.set(x, terrainSurfaceHeight(l, x, z) + 0.01, z);
      const size = 0.65 + hash2(i, 2, l.seed + 701) * 0.35;
      scale.set(size, size, size);
      Quaternion.RotationYawPitchRollToRef(angle, 0, 0, rotation);
      Matrix.ComposeToRef(scale, rotation, position, matrix);
      for (const value of matrix.m) matrices.push(value);
    }
    if (!matrices.length) return;
    const mesh = groundAccent(l.theme, this.scene);
    mesh.name = `ground-accents:${l.id}`;
    mesh.material = this.mats.get(l.theme === "frost" ? "ice" : l.theme === "marsh" ? "grassDark" : l.theme === "tropical" ? "leafDark" : "rockDark");
    mesh.isPickable = false; mesh.receiveShadows = true;
    mesh.thinInstanceSetBuffer("matrix", new Float32Array(matrices), 16, true);
    mesh.thinInstanceRefreshBoundingInfo(false);
  }
}

function offshoreCell(x: number, z: number): string { return `sea:${Math.floor(x / 128)}:${Math.floor(z / 128)}`; }

function dressingClearance(l: Landmass): (x: number, z: number) => boolean {
  const extent = l.radius * 1.75 + 4;
  const colliders = COLLIDERS.filter(c => c.kind === "cyl"
    ? Math.abs(c.x - l.cx) < extent + c.r && Math.abs(c.z - l.cz) < extent + c.r
    : c.minX < l.cx + extent && c.maxX > l.cx - extent && c.minZ < l.cz + extent && c.maxZ > l.cz - extent);
  return (x, z) => {
    if (l.id === "lantern") {
      for (const path of LANTERN_PATHS) for (let i = 1; i < path.points.length; i++) {
        const a = path.points[i - 1]!, b = path.points[i]!, dx = b.x - a.x, dz = b.z - a.z;
        const length2 = dx * dx + dz * dz;
        if (length2 < 0.01) continue;
        const t = clamp(((x - a.x) * dx + (z - a.z) * dz) / length2, 0, 1);
        if ((x - a.x - t * dx) ** 2 + (z - a.z - t * dz) ** 2 < (path.width / 2 + 0.65) ** 2) return false;
      }
    }
    for (const dig of l.digSpots) if ((x - l.cx - dig.x) ** 2 + (z - l.cz - dig.z) ** 2 < 4) return false;
    for (const c of colliders) {
      if (c.kind === "cyl") { if ((x - c.x) ** 2 + (z - c.z) ** 2 < (c.r + 2) ** 2) return false; }
      else if (x > c.minX - 2 && x < c.maxX + 2 && z > c.minZ - 2 && z < c.maxZ + 2) return false;
    }
    return true;
  };
}

/** Bent, tapering blades with longitudinal leaf UVs; no alpha cutouts or tiny casters. */
function bladeClump(name: string, theme: IslandTheme, variant: number, scene: Scene): Mesh {
  const positions: number[] = [], colors: number[] = [], uvs: number[] = [], indices: number[] = [], normals: number[] = [];
  const palette = TERRAIN[theme];
  const root = Color3.FromHexString(palette.grassDark).scale(0.8).toLinearSpace();
  const tip = Color3.FromHexString(palette.grass).scale(1.15).toLinearSpace();
  const blades = variant ? 7 : 5, repeat = SURFACE_PROFILES.leaf.metres;
  for (let i = 0; i < blades; i++) {
    const angle = i * 2.399 + variant, c = Math.cos(angle), s = Math.sin(angle);
    const height = (theme === "marsh" ? 0.6 : 0.44) * (0.8 + hash2(i, variant, 719) * 0.4);
    const width = theme === "marsh" ? 0.045 : 0.075, lean = 0.15 + hash2(i, variant, 913) * 0.16;
    const base = positions.length / 3;
    for (const [x, y, z] of [[-width, 0, 0], [width, 0, 0], [-width * 0.55, height * 0.55, lean * 0.3], [width * 0.55, height * 0.55, lean * 0.3], [0, height, lean]]) {
      positions.push(x! * c + z! * s, y!, -x! * s + z! * c);
      uvs.push((x! + width) / repeat, y! / repeat);
      const tint = Color3.Lerp(root, tip, y! / height);
      colors.push(tint.r, tint.g, tint.b, 1);
    }
    indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3, base + 2, base + 4, base + 3);
  }
  VertexData.ComputeNormals(positions, indices, normals);
  const data = new VertexData(); data.positions = positions; data.colors = colors; data.uvs = uvs; data.indices = indices; data.normals = normals;
  const mesh = new Mesh(name, scene); data.applyToMesh(mesh);
  return mesh;
}

function groundAccent(theme: IslandTheme, scene: Scene): Mesh {
  const positions: number[] = [], indices: number[] = [], uvs: number[] = [], normals: number[] = [];
  const leafy = theme === "tropical" || theme === "marsh";
  const repeat = SURFACE_PROFILES[theme === "frost" ? "ice" : theme === "marsh" ? "ground" : leafy ? "leaf" : "rock"].metres;
  if (leafy) {
    for (let i = 0; i < 3; i++) {
      const a = i * 2.1, c = Math.cos(a), s = Math.sin(a), base = positions.length / 3;
      for (const [x, y, z] of [[0, 0, 0], [-0.09, 0.07, 0.3], [0.09, 0.07, 0.3], [0, 0.025, 0.7]]) {
        positions.push(x! * c + z! * s, y!, -x! * s + z! * c);
        uvs.push((x! + 0.1) / repeat, z! / repeat);
      }
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
  } else {
    positions.push(-0.18, 0, -0.12, 0.2, 0, -0.08, 0.08, 0, 0.2, -0.04, 0.18, 0.02);
    indices.push(0, 1, 3, 1, 2, 3, 2, 0, 3, 0, 2, 1);
    uvs.push(0, 0, 0.38 / repeat, 0, 0.26 / repeat, 0.32 / repeat, 0.14 / repeat, 0.18 / repeat);
  }
  VertexData.ComputeNormals(positions, indices, normals);
  // Back-face triangles share the upward normal; averaging opposite faces would cancel it.
  if (leafy) for (let i = 0, end = indices.length; i < end; i += 3) indices.push(indices[i]!, indices[i + 2]!, indices[i + 1]!);
  const data = new VertexData(); data.positions = positions; data.indices = indices; data.uvs = uvs; data.normals = normals;
  const mesh = new Mesh("ground-accent", scene); data.applyToMesh(mesh);
  if (!leafy) mesh.convertToFlatShadedMesh();
  return mesh;
}

function cylinderUVs(mesh: Mesh, radius: number, height: number, repeat: number): void {
  const uv = mesh.getVerticesData("uv")!, positions = mesh.getVerticesData("position")!, normals = mesh.getVerticesData("normal")!;
  for (let i = 0; i < positions.length / 3; i++) {
    if (Math.abs(normals[i * 3 + 1]!) > 0.9) {
      uv[i * 2] = positions[i * 3]! / repeat; uv[i * 2 + 1] = positions[i * 3 + 2]! / repeat;
    } else { uv[i * 2] = uv[i * 2]! * TAU * radius / repeat; uv[i * 2 + 1] = uv[i * 2 + 1]! * height / repeat; }
  }
  mesh.setVerticesData("uv", uv, false);
}

/** Shallow edge chamfers preserve the exact pier footprint and walkable top. */
function bevelledSlab(name: string, width: number, depth: number, height: number, scene: Scene): Mesh {
  const positions: number[] = [], indices: number[] = [], normals: number[] = [];
  for (const [y, inset] of [[-height, 0.025], [-height + 0.025, 0], [-0.025, 0], [0, 0.025]]) {
    for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) positions.push(x! * (width / 2 - inset!), y!, z! * (depth / 2 - inset!));
  }
  for (let ring = 0; ring < 3; ring++) for (let i = 0; i < 4; i++) {
    const a = ring * 4 + i, b = ring * 4 + (i + 1) % 4;
    indices.push(a, b, a + 4, b, b + 4, a + 4);
  }
  indices.push(12, 13, 14, 12, 14, 15, 0, 2, 1, 0, 3, 2);
  VertexData.ComputeNormals(positions, indices, normals);
  const data = new VertexData(); data.positions = positions; data.indices = indices; data.normals = normals;
  const mesh = new Mesh(name, scene); data.applyToMesh(mesh); mesh.convertToFlatShadedMesh();
  return mesh;
}

/** Sixteen fractured sectors stay within .4m of the unchanged cylinder silhouette. */
function fittedRock(name: string, radius: number, top: number, scene: Scene): Mesh {
  const positions: number[] = [], indices: number[] = [], uvs: number[] = [], colors: number[] = [], normals: number[] = [];
  const repeat = SURFACE_PROFILES.rock.metres;
  const levels = [-3, top * 0.2, top * 0.65, top];
  for (let j = 0; j < levels.length; j++) for (let i = 0; i <= 16; i++) {
    const a = i % 16 / 16 * TAU;
    const r = radius + (hash2(i % 16, j, Math.round(radius * 93)) - 0.5) * 0.3;
    positions.push(Math.cos(a) * r, levels[j]!, Math.sin(a) * r);
    uvs.push(i / 16 * TAU * radius / repeat, levels[j]! / repeat);
    const tint = j === 1 ? 1.12 : 0.9 + hash2(i % 16, j, 47) * 0.12;
    colors.push(tint, tint, tint, 1);
  }
  for (let j = 0; j < levels.length - 1; j++) for (let i = 0; i < 16; i++) {
    const a = j * 17 + i, b = a + 1, c = a + 17, d = c + 1;
    indices.push(a, b, c, b, d, c);
  }
  const centre = positions.length / 3;
  positions.push(0, top, 0); uvs.push(0, 0); colors.push(0.94, 0.94, 0.94, 1);
  // Caps have independent planar loops, never a circumference seam through their centre.
  for (let i = 0; i <= 16; i++) {
    const source = (3 * 17 + i) * 3;
    positions.push(positions[source]!, top, positions[source + 2]!);
    uvs.push(positions[source]! / repeat, positions[source + 2]! / repeat);
    colors.push(0.94, 0.94, 0.94, 1);
    if (i < 16) indices.push(centre, centre + i + 1, centre + i + 2);
  }
  VertexData.ComputeNormals(positions, indices, normals);
  const data = new VertexData(); data.positions = positions; data.indices = indices; data.uvs = uvs; data.colors = colors; data.normals = normals;
  const mesh = new Mesh(name, scene); data.applyToMesh(mesh); mesh.convertToFlatShadedMesh();
  return mesh;
}
